import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  AlertTriangle,
  CheckCircle,
  Shield,
  Globe,
  FileText,
  BellRing,
  Ban,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { StatCard } from "@/components/admin/StatCard";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import {
  getComplianceRules,
  getViolations,
} from "@/lib/admin/queries";
import {
  updateComplianceRule,
  updateGlobalDefaultNightCap,
  resolveViolation,
  notifyViolatingHost,
  deactivateUnit,
} from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { StrataConfig, Host } from "@/types/database";

// ─── Types ────────────────────────────────────────────────────────────────────

type ComplianceRuleRow = StrataConfig & {
  organization?: { id: string; name: string; address?: string | null } | null;
};

type ViolationRow = Host & {
  profile?: { id: string; full_name: string; email: string } | null;
  unit?: {
    id: string;
    unit_number: string;
    status: string;
    organization?: { id: string; name: string } | null;
  } | null;
};

type RuleFormValues = {
  max_nights_per_year: number;
  require_council_approval_per_booking: boolean;
  require_provincial_registration: boolean;
  require_business_licence: boolean;
  min_stay_nights: number;
  max_guest_count: number;
  max_consecutive_nights: number;
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminCompliancePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Rules tab state
  const [globalCap, setGlobalCap] = useState<number | "">("");
  const [editRule, setEditRule] = useState<ComplianceRuleRow | null>(null);
  const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
  const [rulesPage, setRulesPage] = useState(1);

  // Violations tab state
  const [resolveTarget, setResolveTarget] = useState<ViolationRow | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<ViolationRow | null>(null);
  const [violationsPage, setViolationsPage] = useState(1);

  // Rule edit form
  const ruleForm = useForm<RuleFormValues>();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const { data: rulesResult, isLoading: rulesLoading } = useQuery({
    queryKey: ["admin", "compliance-rules", rulesPage],
    queryFn: () => getComplianceRules(rulesPage, 25),
  });

  const { data: violationsResult, isLoading: violationsLoading } = useQuery({
    queryKey: ["admin", "violations", violationsPage],
    queryFn: () => getViolations(violationsPage, 25),
  });

  const rules = (rulesResult?.data ?? []) as ComplianceRuleRow[];
  const rulesTotal = rulesResult?.count ?? 0;
  const violations = (violationsResult?.data ?? []) as ViolationRow[];
  const violationsTotal = violationsResult?.count ?? 0;

  // Violation type counts
  const overCapCount = violations.filter(
    (v) => v.nights_used > v.night_limit
  ).length;
  const suspendedCount = violations.filter((v) => v.status === "suspended").length;
  const expiredCount = violations.filter((v) => v.status === "expired").length;

  // ─── Mutations ────────────────────────────────────────────────────────────

  const globalCapMutation = useMutation({
    mutationFn: (cap: number) => updateGlobalDefaultNightCap(cap, user!.id),
    onSuccess: () => {
      toast.success("Global default night cap updated");
      setGlobalCap("");
      queryClient.invalidateQueries({ queryKey: ["admin", "compliance-rules"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateRuleMutation = useMutation({
    mutationFn: (vars: { orgId: string; data: Partial<StrataConfig> }) =>
      updateComplianceRule(vars.orgId, vars.data, user!.id),
    onSuccess: () => {
      toast.success("Compliance rule updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "compliance-rules"] });
      setEditRule(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (vars: { orgIds: string[]; data: Partial<StrataConfig> }) => {
      const results = await Promise.all(
        vars.orgIds.map((orgId) =>
          updateComplianceRule(orgId, vars.data, user!.id)
        )
      );
      return results;
    },
    onSuccess: () => {
      toast.success(`Bulk update applied to ${selectedBuildings.length} buildings`);
      setSelectedBuildings([]);
      queryClient.invalidateQueries({ queryKey: ["admin", "compliance-rules"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const resolveMutation = useMutation({
    mutationFn: (hostId: string) => resolveViolation(hostId, user!.id),
    onSuccess: () => {
      toast.success("Violation resolved");
      queryClient.invalidateQueries({ queryKey: ["admin", "violations"] });
      setResolveTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const notifyMutation = useMutation({
    mutationFn: (hostId: string) => notifyViolatingHost(hostId, user!.id),
    onSuccess: () => toast.success("Host notified"),
    onError: (err: Error) => toast.error(err.message),
  });

  const suspendUnitMutation = useMutation({
    mutationFn: (unitId: string) => deactivateUnit(unitId, user!.id),
    onSuccess: () => {
      toast.success("Unit suspended");
      queryClient.invalidateQueries({ queryKey: ["admin", "violations"] });
      setSuspendTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function openEditRule(rule: ComplianceRuleRow) {
    ruleForm.reset({
      max_nights_per_year: rule.max_nights_per_year,
      require_council_approval_per_booking: rule.require_council_approval_per_booking,
      require_provincial_registration: rule.require_provincial_registration,
      require_business_licence: rule.require_business_licence,
      min_stay_nights: rule.min_stay_nights,
      max_guest_count: rule.max_guest_count,
      max_consecutive_nights: rule.max_consecutive_nights ?? 0,
    });
    setEditRule(rule);
  }

  function handleRuleFormSubmit(values: RuleFormValues) {
    if (!editRule) return;
    updateRuleMutation.mutate({
      orgId: editRule.organization_id,
      data: {
        max_nights_per_year: values.max_nights_per_year,
        require_council_approval_per_booking: values.require_council_approval_per_booking,
        require_provincial_registration: values.require_provincial_registration,
        require_business_licence: values.require_business_licence,
        min_stay_nights: values.min_stay_nights,
        max_guest_count: values.max_guest_count,
        max_consecutive_nights: values.max_consecutive_nights || null,
      },
    });
  }

  function handleExportViolations() {
    const rows = violations.map((v) => ({
      building: v.unit?.organization?.name ?? "",
      unit: v.unit?.unit_number ?? "",
      host: v.profile?.full_name ?? "",
      email: v.profile?.email ?? "",
      nights_used: v.nights_used,
      night_cap: v.night_limit,
      nights_over: Math.max(0, v.nights_used - v.night_limit),
      status: v.status,
    }));
    const header = Object.keys(rows[0] ?? {}).join(",");
    const csv = [header, ...rows.map((r) => Object.values(r).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "violations-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Rules Columns ────────────────────────────────────────────────────────

  const rulesColumns = [
    {
      key: "building",
      label: "Building",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return (
          <span className="font-medium text-gray-900 truncate max-w-[160px] block">
            {rule.organization?.name ?? "—"}
          </span>
        );
      },
    },
    {
      key: "max_nights_per_year",
      label: "Night Cap",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return <span className="tabular-nums font-semibold whitespace-nowrap">{rule.max_nights_per_year}</span>;
      },
    },
    {
      key: "require_council_approval_per_booking",
      label: "Approval Required",
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return rule.require_council_approval_per_booking ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <span className="text-gray-300 text-sm">—</span>
        );
      },
    },
    {
      key: "require_provincial_registration",
      label: "Permit Required",
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return rule.require_provincial_registration ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <span className="text-gray-300 text-sm">—</span>
        );
      },
    },
    {
      key: "max_consecutive_nights",
      label: "Max Consecutive",
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return (
          <span className="tabular-nums text-sm whitespace-nowrap">
            {rule.max_consecutive_nights ?? <span className="text-gray-400">—</span>}
          </span>
        );
      },
    },
    {
      key: "updated_at",
      label: "Last Updated",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return (
          <span className="text-xs tabular-nums text-gray-400 whitespace-nowrap">
            {format(new Date(rule.updated_at), "MMM d, yyyy")}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row: Record<string, unknown>) => {
        const rule = row as unknown as ComplianceRuleRow;
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={() => openEditRule(rule)}
            className="h-7 text-xs"
          >
            Edit
          </Button>
        );
      },
    },
  ];

  // ─── Violations Columns ───────────────────────────────────────────────────

  const violationsColumns = [
    {
      key: "building",
      label: "Building",
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        return (
          <span className="font-medium text-sm text-gray-900 truncate max-w-[160px] block">
            {v.unit?.organization?.name ?? "—"}
          </span>
        );
      },
    },
    {
      key: "unit",
      label: "Unit",
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        return <span className="font-mono text-sm whitespace-nowrap">{v.unit?.unit_number ?? "—"}</span>;
      },
    },
    {
      key: "host",
      label: "Host",
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        return (
          <div>
            <p className="text-sm font-medium">{v.profile?.full_name ?? "—"}</p>
            <p className="text-xs text-gray-400">{v.profile?.email ?? ""}</p>
          </div>
        );
      },
    },
    {
      key: "nights_over",
      label: "Nights Over Cap",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        const over = v.nights_used - v.night_limit;
        return (
          <span className={`tabular-nums font-semibold text-sm whitespace-nowrap ${over > 0 ? "text-red-600" : "text-gray-400"}`}>
            {over > 0 ? `+${over}` : "0"}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        return <StatusBadge status={v.status} size="sm" />;
      },
    },
    {
      key: "updated_at",
      label: "Date Detected",
      sortable: true,
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        return (
          <span className="text-xs tabular-nums text-gray-400 whitespace-nowrap">
            {format(new Date(v.updated_at), "MMM d, yyyy")}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row: Record<string, unknown>) => {
        const v = row as unknown as ViolationRow;
        return (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50 whitespace-nowrap"
              onClick={() => setResolveTarget(v)}
            >
              <CheckCircle className="mr-1 h-3 w-3" />
              Resolve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50 whitespace-nowrap"
              disabled={notifyMutation.isPending}
              onClick={() => notifyMutation.mutate(v.id)}
            >
              <BellRing className="mr-1 h-3 w-3" />
              Notify
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
              onClick={() => setSuspendTarget(v)}
            >
              <Ban className="mr-1 h-3 w-3" />
              Suspend
            </Button>
          </div>
        );
      },
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <h1 className="text-2xl font-bold text-gray-900">Compliance</h1>

      <Tabs defaultValue="rules">
        <TabsList className="mb-4">
          <TabsTrigger value="rules" className="flex items-center gap-2 whitespace-nowrap">
            <Shield className="h-4 w-4" />
            Compliance Rules
          </TabsTrigger>
          <TabsTrigger value="violations" className="flex items-center gap-2 whitespace-nowrap">
            <AlertTriangle className="h-4 w-4" />
            Violations
            {violationsTotal > 0 && (
              <span className="ml-1 rounded-full bg-red-500 px-1.5 py-0.5 text-xs text-white font-semibold">
                {violationsTotal}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── TAB 1: Compliance Rules ─────────────────────────────────── */}
        <TabsContent value="rules" className="space-y-6">
          {/* Global defaults */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-400" />
                Global Default Night Cap
              </CardTitle>
              <CardDescription>
                Set the default night cap applied to all new buildings. Existing
                buildings are not affected unless you use bulk update.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  max={365}
                  className="w-32"
                  placeholder="e.g. 90"
                  value={globalCap}
                  onChange={(e) =>
                    setGlobalCap(e.target.value === "" ? "" : Number(e.target.value))
                  }
                />
                <Button
                  className="whitespace-nowrap"
                  disabled={globalCap === "" || globalCapMutation.isPending}
                  onClick={() => globalCap !== "" && globalCapMutation.mutate(Number(globalCap))}
                >
                  {globalCapMutation.isPending ? "Saving…" : "Save Global Default"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk update banner (when buildings selected) */}
          {selectedBuildings.length > 0 && (
            <div className="flex items-center gap-3 rounded-md bg-blue-50 border border-blue-200 px-4 py-3">
              <span className="text-sm font-medium text-blue-700">
                {selectedBuildings.length} building{selectedBuildings.length !== 1 ? "s" : ""} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto whitespace-nowrap"
                disabled={bulkUpdateMutation.isPending}
                onClick={() => {
                  const cap = Number(window.prompt("Set night cap for selected buildings:"));
                  if (!isNaN(cap) && cap > 0) {
                    bulkUpdateMutation.mutate({
                      orgIds: selectedBuildings,
                      data: { max_nights_per_year: cap },
                    });
                  }
                }}
              >
                Apply Same Rules
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedBuildings([])}
              >
                Clear
              </Button>
            </div>
          )}

          {/* Rules table */}
          <DataTable
            columns={rulesColumns}
            data={rules as unknown as Record<string, unknown>[]}
            loading={rulesLoading}
            totalCount={rulesTotal}
            page={rulesPage}
            pageSize={25}
            onPageChange={setRulesPage}
            selectedRows={selectedBuildings}
            onRowSelect={setSelectedBuildings}
            bulkActions={[]}
            emptyMessage="No compliance rules found."
          />
        </TabsContent>

        {/* ── TAB 2: Violations ───────────────────────────────────────── */}
        <TabsContent value="violations" className="space-y-6">
          {/* Summary stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              title="Total Violations"
              value={violationsTotal}
              icon={<AlertTriangle className="h-5 w-5" />}
              color="red"
              loading={violationsLoading}
            />
            <StatCard
              title="Over Night Cap"
              value={overCapCount}
              icon={<Shield className="h-5 w-5" />}
              color="amber"
              loading={violationsLoading}
            />
            <StatCard
              title="Suspended"
              value={suspendedCount}
              icon={<Ban className="h-5 w-5" />}
              color="red"
              loading={violationsLoading}
            />
            <StatCard
              title="Expired"
              value={expiredCount}
              icon={<FileText className="h-5 w-5" />}
              color="purple"
              loading={violationsLoading}
            />
          </div>

          {/* Violations table */}
          <DataTable
            columns={violationsColumns}
            data={violations as unknown as Record<string, unknown>[]}
            loading={violationsLoading}
            totalCount={violationsTotal}
            page={violationsPage}
            pageSize={25}
            onPageChange={setViolationsPage}
            onExport={handleExportViolations}
            emptyMessage="No violations found."
          />
        </TabsContent>
      </Tabs>

      {/* Edit Rule Sheet */}
      <Sheet open={!!editRule} onOpenChange={(v) => { if (!v) setEditRule(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Compliance Rules</SheetTitle>
            <SheetDescription>
              {editRule?.organization?.name}
            </SheetDescription>
          </SheetHeader>
          <form
            onSubmit={ruleForm.handleSubmit(handleRuleFormSubmit)}
            className="space-y-5 mt-6"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_nights_per_year" className="whitespace-nowrap">Night Cap (per year)</Label>
                <Input
                  id="max_nights_per_year"
                  type="number"
                  min={1}
                  max={365}
                  {...ruleForm.register("max_nights_per_year", { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_stay_nights" className="whitespace-nowrap">Min Stay (nights)</Label>
                <Input
                  id="min_stay_nights"
                  type="number"
                  min={1}
                  {...ruleForm.register("min_stay_nights", { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_guest_count" className="whitespace-nowrap">Max Guests</Label>
                <Input
                  id="max_guest_count"
                  type="number"
                  min={1}
                  {...ruleForm.register("max_guest_count", { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_consecutive_nights" className="whitespace-nowrap">Max Consecutive Nights</Label>
                <Input
                  id="max_consecutive_nights"
                  type="number"
                  min={0}
                  placeholder="Unlimited"
                  {...ruleForm.register("max_consecutive_nights", { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <p className="text-sm font-semibold text-gray-700">Requirements</p>

              <div className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Council Approval per Booking</p>
                  <p className="text-xs text-gray-500">Each booking requires manual council approval</p>
                </div>
                <Switch
                  checked={ruleForm.watch("require_council_approval_per_booking")}
                  onCheckedChange={(v) =>
                    ruleForm.setValue("require_council_approval_per_booking", v)
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Provincial Registration</p>
                  <p className="text-xs text-gray-500">Host must have a valid STR registration number</p>
                </div>
                <Switch
                  checked={ruleForm.watch("require_provincial_registration")}
                  onCheckedChange={(v) =>
                    ruleForm.setValue("require_provincial_registration", v)
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Business Licence</p>
                  <p className="text-xs text-gray-500">Host must have a valid municipal business licence</p>
                </div>
                <Switch
                  checked={ruleForm.watch("require_business_licence")}
                  onCheckedChange={(v) =>
                    ruleForm.setValue("require_business_licence", v)
                  }
                />
              </div>
            </div>

            <SheetFooter className="pt-4">
              <Button
                variant="outline"
                type="button"
                className="whitespace-nowrap"
                onClick={() => setEditRule(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateRuleMutation.isPending} className="whitespace-nowrap">
                {updateRuleMutation.isPending ? "Saving…" : "Save Rules"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Resolve Violation */}
      <ConfirmModal
        open={!!resolveTarget}
        onClose={() => setResolveTarget(null)}
        onConfirm={() => resolveTarget && resolveMutation.mutate(resolveTarget.id)}
        title="Mark Violation Resolved"
        description={`Mark the violation for host ${resolveTarget?.profile?.full_name ?? ""} as resolved? Their status will be set to approved.`}
        loading={resolveMutation.isPending}
        variant="warning"
      />

      {/* Suspend Unit */}
      <ConfirmModal
        open={!!suspendTarget}
        onClose={() => setSuspendTarget(null)}
        onConfirm={() =>
          suspendTarget?.unit?.id && suspendUnitMutation.mutate(suspendTarget.unit.id)
        }
        title="Suspend Unit"
        description={`Suspend Unit ${suspendTarget?.unit?.unit_number ?? ""}? This will deactivate the unit and the host will lose booking privileges.`}
        requireTyping
        loading={suspendUnitMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
